import React, { useState, useEffect, useMemo } from 'react';
import Papa from 'papaparse';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';

// Layout Components
import Navbar from './components/Navbar';
import { TabNavigation, TabType } from './components/layout/TabNavigation';
import { Footer } from './components/layout/Footer';

// Overview Components
import { HeroMetrics } from './components/overview/HeroMetrics';
import { QuickStatsGrid } from './components/overview/QuickStatsGrid';
import { WelcomeSection } from './components/overview/WelcomeSection';
import { GardenWeather } from './components/GardenWeather';

// Environmental Impact Components
import { OxygenSection } from './components/environmental/OxygenSection';
import { CarbonSection } from './components/environmental/CarbonSection';
import { AirQualitySection } from './components/environmental/AirQualitySection';
import { BiodiversitySection } from './components/environmental/BiodiversitySection';
import { AdditionalBenefits } from './components/environmental/AdditionalBenefits';

// Gallery Components
import { GalleryTab } from './components/gallery/GalleryTab';

// References Component
import { ReferencesTab } from './components/references/ReferencesTab';

// Species Component
import { EnhancedSpeciesTab } from './components/species/EnhancedSpeciesTab';

// Existing Components (to be gradually replaced)
import CarbonCalculator from './components/CarbonCalculator';

// Utilities
import { Tree } from './types/tree';
import {
  calculateComprehensiveImpact,
} from './utils/environmentalCalculations';
import {
  calculateBiodiversityAssessment,
  calculateConservationMetrics,
} from './utils/biodiversityMetrics';

import './App.css';

// Map CSV columns from EG_all_carbon.csv format to Tree interface format
interface RawCSVRow {
  tagID: string;
  lat: string;
  long: string;
  genus?: string;
  species: string;
  sp_name?: string;
  common_name: string;
  family?: string;
  gbh: string;
  height: string;
  agb: string;
  bgb: string;
  biomass: string;
  carbon: string;
  co2e: string;
  Crown_diameter?: string;
  Threat_status?: string;
  Native?: string;
  Invasive?: string;
  Endemism?: string;
  Uses?: string;
  Recent_Accepted_Name?: string;
}

function mapCSVRowToTree(row: RawCSVRow): Tree {
  return {
    TagID: row.tagID || '',
    Latitude: row.lat ? row.lat.replace(',', '') : '', // Remove trailing comma if present
    Longitude: row.long || '',
    Species: row.sp_name || row.species || '',
    Common_Name: row.common_name || '',
    Family: row.family || '',
    GBH: row.gbh || '',
    Height: row.height || '',
    Crown_diameter: row.Crown_diameter || '',
    Notes: '',
    Carbon: row.carbon || '',
    Biomass: row.biomass || '',
    AGB: row.agb || '',
    BGB: row.bgb || '',
    co2: row.co2e || '',
    Threat_status: row.Threat_status || '',
    Native: row.Native || '',
    Ec_Dc: '',
    Endemism: row.Endemism || '',
    Uses: row.Uses || '',
    Canopy: '',
    Invasive: row.Invasive || '',
    Time: '',
    User: '',
    Recent_Accepted_Name: row.Recent_Accepted_Name || '',
  };
}

// Fix Leaflet marker icons
// @ts-ignore
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
  iconUrl: require('leaflet/dist/images/marker-icon.png'),
  shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
});

function App() {
  // State
  const [treeData, setTreeData] = useState<Tree[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<TabType>('overview');
  const [showReferences, setShowReferences] = useState(false);

  // Load CSV Data
  useEffect(() => {
    const loadData = async () => {
      try {
        setIsLoading(true);
        const response = await fetch(`${process.env.PUBLIC_URL}/data/EG_all_carbon.csv`);
        const csvText = await response.text();

        Papa.parse(csvText, {
          header: true,
          dynamicTyping: false, // Keep as strings for consistent mapping
          skipEmptyLines: true,
          complete: (results) => {
            const rawRows = results.data as RawCSVRow[];
            const trees = rawRows.map(mapCSVRowToTree);
            setTreeData(trees);
            setIsLoading(false);
            console.log(`Loaded ${trees.length} trees from CSV`);
          },
          error: (error: Error) => {
            console.error('Error parsing CSV:', error);
            setIsLoading(false);
          },
        });
      } catch (error) {
        console.error('Error loading CSV:', error);
        setIsLoading(false);
      }
    };

    loadData();
  }, []);

  // Calculate all metrics
  const metrics = useMemo(() => {
    if (treeData.length === 0) {
      return null;
    }

    const comprehensive = calculateComprehensiveImpact(treeData);
    const biodiversity = calculateBiodiversityAssessment(treeData);
    const conservation = calculateConservationMetrics(treeData);

    // Count species
    const speciesSet = new Set(treeData.map(t => t.Species).filter(Boolean));

    return {
      comprehensive,
      biodiversity,
      conservation,
      speciesCount: speciesSet.size,
    };
  }, [treeData]);

  // Handle tab changes
  const handleTabChange = (tab: TabType) => {
    setActiveTab(tab);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleShowReferences = () => {
    setShowReferences(true);
    setActiveTab('references');
  };

  const handleExploreGallery = () => {
    setActiveTab('gallery');
  };

  // Loading state
  if (isLoading) {
    return (
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        height: '100vh',
        fontFamily: 'var(--font-body)',
        fontSize: 'var(--font-size-xl)',
        color: 'var(--forest-dark)',
      }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ marginBottom: '1rem' }}>🌳</div>
          <div>Loading Empress Botanical Garden data...</div>
        </div>
      </div>
    );
  }

  if (!metrics) {
    return (
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        height: '100vh',
        fontFamily: 'var(--font-body)',
        color: 'var(--alert-red)',
      }}>
        Error loading data. Please refresh the page.
      </div>
    );
  }

  return (
    <div className="app">
      <Navbar />

      <TabNavigation
        activeTab={activeTab}
        onTabChange={handleTabChange}
        showReferences={showReferences}
      />

      <main className="app__content">
        {/* OVERVIEW TAB */}
        {activeTab === 'overview' && (
          <>
            <HeroMetrics
              totalTrees={treeData.length}
              speciesCount={metrics.speciesCount}
              totalCarbonTonnes={metrics.comprehensive.carbon.totalCO2Tonnes}
              oxygenLiters={metrics.comprehensive.oxygen.totalOxygenLiters}
            />
            <GardenWeather googleSheetUrl="https://docs.google.com/spreadsheets/d/e/2PACX-1vTkBkFUPAsoF8J-QAHxQmdfeSAQha1hLd4MRhkj5rsTT2rNdsInKNdzOjDv6nVusjI5dTRgUuCLZRFA/pub?output=csv" />
            <QuickStatsGrid
              nativeCount={metrics.conservation.nativeCount}
              nativeProportion={metrics.conservation.nativeProportion}
              endemicCount={metrics.conservation.endemicCount}
              threatenedCount={metrics.conservation.threatenedCount}
            />
            <WelcomeSection onExploreGallery={handleExploreGallery} />
          </>
        )}

        {/* GALLERY TAB */}
        {activeTab === 'gallery' && (
          <GalleryTab trees={treeData} />
        )}

        {/* SPECIES TAB */}
        {activeTab === 'species' && (
          <EnhancedSpeciesTab trees={treeData} />
        )}

        {/* ENVIRONMENTAL IMPACT TAB */}
        {activeTab === 'environmental' && (
          <>
            <OxygenSection metrics={metrics.comprehensive.oxygen} />
            <CarbonSection metrics={metrics.comprehensive.carbon} />
            <AirQualitySection metrics={metrics.comprehensive.airQuality} />
            <BiodiversitySection assessment={metrics.biodiversity} trees={treeData} />
            <AdditionalBenefits
              cooling={metrics.comprehensive.cooling}
              stormwater={metrics.comprehensive.stormwater}
              onShowReferences={handleShowReferences}
            />
          </>
        )}

        {/* CARBON CALCULATOR TAB */}
        {activeTab === 'carbon' && (
          <CarbonCalculator />
        )}

        {/* REFERENCES TAB */}
        {activeTab === 'references' && (
          <ReferencesTab />
        )}
      </main>

      <Footer />
    </div>
  );
}

export default App;
